import { For, type JSX } from "solid-js"
import { useLanguage } from "@/context/language"
import { CoworkBrand } from "./brand"
import "./auth-theme.css"

const authLocales = ["en", "ru", "et"] as const

export const authInputClass =
  "cowork-auth-input h-12 w-full min-w-0 rounded-[3px] border px-3.5 text-[15px] outline-none"
export const authLabelClass = "mb-5 flex flex-col gap-2 text-[14px] font-medium"
export const authLabelStyle = { color: "var(--auth-body)" }
export const authMutedStyle = { color: "var(--auth-body)" }
export const authFaintStyle = { color: "var(--auth-muted)" }
export const authPrimaryButtonClass =
  "cowork-auth-primary mt-6 flex min-h-12 w-full cursor-pointer items-center justify-center rounded-[3px] px-4 py-3 text-center text-[15px] font-medium leading-5 disabled:cursor-not-allowed disabled:opacity-50"
export const authPrimaryButtonStyle = { color: "var(--auth-white)" }
export const authLinkClass = "cowork-auth-link cursor-pointer rounded-[2px] font-medium"
export const authErrorClass = "cowork-auth-error mt-4 rounded border p-3 text-[14px] leading-5"
export const authNoticeClass = "cowork-auth-notice mt-4 rounded border p-3 text-[14px] leading-5"
export const authLoadingClass = "cowork-auth-loading flex min-h-dvh items-center justify-center text-[14px]"

export function CoworkAuthShell(props: { children: JSX.Element }) {
  const language = useLanguage()
  return (
    <div data-cowork-auth class="flex h-dvh flex-col overflow-x-hidden overflow-y-auto">
      <header class="shrink-0 border-b border-[var(--auth-border)]">
        <div class="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-4 px-4 py-4 sm:px-8 sm:py-5">
          <CoworkBrand appearance="light" />
          <div>
            <label class="sr-only" for="cowork-auth-language">
              {language.t("settings.general.row.language.title")}
            </label>
            <select
              id="cowork-auth-language"
              data-action="auth-language"
              class="h-10 max-w-36 cursor-pointer rounded-[3px] border border-[var(--auth-input)] bg-[var(--auth-paper)] px-3 text-[14px] text-[var(--auth-text)] outline-none hover:border-[var(--auth-gold)]"
              value={language.locale()}
              onChange={(event) => language.setLocale(event.currentTarget.value as (typeof authLocales)[number])}
            >
              <For each={authLocales}>{(locale) => <option value={locale}>{language.label(locale)}</option>}</For>
            </select>
          </div>
        </div>
      </header>
      <main class="mx-auto my-auto grid w-full max-w-[1200px] shrink-0 items-center gap-10 px-4 py-8 sm:px-8 sm:py-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:py-16">
        <section class="hidden min-w-0 lg:block">
          <div>
            <h2 class="max-w-[560px] text-[44px] font-medium leading-[1.08] tracking-[-0.025em] text-[var(--auth-text)]">
              {language.t("cowork.login.heroTitle")}
            </h2>
            <p class="mt-6 max-w-[520px] text-[17px] leading-7" style={authMutedStyle}>
              {language.t("cowork.login.heroDescription")}
            </p>
          </div>
          <div class="mt-10 grid gap-5">
            <Feature
              index="1"
              title="cowork.login.feature.agents.title"
              description="cowork.login.feature.agents.description"
            />
            <Feature
              index="2"
              title="cowork.login.feature.connected.title"
              description="cowork.login.feature.connected.description"
            />
            <Feature
              index="3"
              title="cowork.login.feature.realWork.title"
              description="cowork.login.feature.realWork.description"
            />
          </div>
        </section>
        <div class="mx-auto w-full min-w-0 max-w-[520px] lg:max-w-none">
          <div class="mb-7 lg:hidden">
            <p class="text-[30px] font-medium leading-[1.15] tracking-[-0.025em] text-[var(--auth-text)]">
              {language.t("cowork.login.heroTitle")}
            </p>
          </div>
          <div
            data-auth-panel
            class="rounded-lg border border-[var(--auth-border)] bg-[var(--auth-white)] p-6 sm:p-8 lg:p-10"
          >
            {props.children}
          </div>
        </div>
      </main>
    </div>
  )
}

export function CoworkAuthHeading(props: { title: string; description: string }) {
  const language = useLanguage()
  return (
    <div class="mb-8">
      <div class="mb-4 text-[12px] font-medium leading-5 tracking-[0.08em] text-[var(--auth-gold)] uppercase">
        {language.t("cowork.login.badge")}
      </div>
      <h1 class="text-[32px] font-medium leading-[1.15] tracking-[-0.02em] text-[var(--auth-text)]">{props.title}</h1>
      <p class="mt-3 text-[15px] leading-6" style={authMutedStyle}>
        {props.description}
      </p>
    </div>
  )
}

type CopyKey = keyof typeof import("@/i18n/en").dict

function Feature(props: { index: string; title: CopyKey; description: CopyKey }) {
  const language = useLanguage()
  return (
    <div class="border-t border-[var(--auth-border)] pt-5">
      <div class="flex items-baseline gap-3 text-[var(--auth-text)]">
        <span aria-hidden="true" class="shrink-0 text-[13px] font-medium text-[var(--auth-gold)]">
          {props.index}
        </span>
        <h3 class="text-[17px] font-medium">{language.t(props.title)}</h3>
      </div>
      <p class="mt-2 text-[14px] leading-6" style={authMutedStyle}>
        {language.t(props.description)}
      </p>
    </div>
  )
}
